import React, { useCallback, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    useMediaQuery,
    useTheme,
    Snackbar,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { styled } from '@mui/system';
import { alpha } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import Typography from '@mui/material/Typography';
import { DataGridPremium, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import {
    getOrganizationsAndSubscriptionData,
    getOrganizationsAndSubscriptionDataCSV,
    GetOrganizationsAndSubscriptionDataParams,
    createOrganization,
    CreateOrganizationParams,
} from '../organizations.api';
import { Organization } from '../organizations.types';
import { Organization as SimpleOrganization } from 'utils';
import { GridColDef } from '@mui/x-data-grid-premium';
import type { GridColumnGroupingModel } from '@mui/x-data-grid';
import { adminGetAllOrganizations } from '../../../admin.organizations.api';
import { debounce } from 'lodash';
import OrganizationMap from '../components/OrganizationMap';
import InfoIcon from '@mui/icons-material/Info';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SubscriptionTrends from '../components/SubscriptionTrends';
import MrrMovement from '../components/MrrMovement';
import AddIcon from '@mui/icons-material/Add';
import CreateOrganizationDialog from '../components/CreateOrganizationDialog';
import OrganizationsMetricsBiPanel from '../components/OrganizationsMetricsBiPanel';
import { generateOrgSubscriptionReport } from 'components/SubscriptionReport/generateSubscriptionReport';
import { Subscription } from '../organizations.types';

const ResponsiveFormItem = styled(Box)(({ theme }) => ({
    width: 'auto',
    flexGrow: 0,
    margin: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
        width: 'calc(100% - 16px)',
        maxWidth: 'none',
    },
}));

const ResponsiveForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    margin: theme.spacing(-1),
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
}));

type SelectedView = 'metrics' | 'maps' | 'trends' | 'mrr_movement';

/** Tier keys from analytics SQL (e.g. no_tier); human-readable for grid cells */
function formatSubscriptionTierLabel(raw: string | null | undefined): string {
    if (raw == null || raw === '') return 'No tier';
    const norm = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
    const labels: Record<string, string> = {
        gold: 'Gold',
        silver: 'Silver',
        bronze: 'Bronze',
        no_tier: 'No tier',
    };
    return labels[norm] ?? raw;
}

const ORG_METRICS_COLUMN_GROUPING_MODEL: GridColumnGroupingModel = [
    {
        groupId: 'org_info',
        headerName: 'Org Info',
        headerAlign: 'center',
        children: [
            { field: 'name' },
            { field: 'created_at' },
            { field: 'sponsor_name' },
            { field: 'athlete_count' },
        ],
    },
    {
        groupId: 'subscription_status',
        headerName: 'Subscription Status',
        headerAlign: 'center',
        children: [
            { field: 'subscription_tier' },
            { field: 'stripe_sub_link' },
            { field: 'amount_past_due' },
        ],
    },
    {
        groupId: 'product_adoption',
        headerName: 'Product Adoption',
        headerAlign: 'center',
        children: [
            { field: 'uploads_last_30_days' },
            { field: 'uploads_last_60_days' },
            { field: 'uploads_last_90_days' },
        ],
    },
    {
        groupId: 'commissions',
        headerName: 'Commissions',
        headerAlign: 'center',
        children: [
            { field: 'total_commissions' },
            { field: 'marketplace_commissions' },
            { field: 'pib_bonus_commissions' },
            { field: 'residual_commissions' },
            { field: 'check_match_commissions' },
        ],
    },
    {
        groupId: 'pod_allocations',
        headerName: 'Pod Allocations and Subscriptions',
        headerAlign: 'center',
        children: [
            { field: 'pods_org' },
            { field: 'org_pod_subscriptions' },
            { field: 'pods_athlete' },
            { field: 'athlete_pod_subscriptions' },
        ],
    },
];

const OrganizationsList = () => {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedView, setSelectedView] = useState<SelectedView>('metrics');
    const [filterOrganizations, setFilterOrganizations] = useState<SimpleOrganization[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [searchParams, setSearchParams] = useState<GetOrganizationsAndSubscriptionDataParams>({
        organization_uuid: '',
        tier: 'all',
        sort: '-uploads_last_30_days',
        page: 1,
        per_page: 50,
        start_date: '',
        end_date: '',
        is_problematic_account: false,
    });

    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: 'uploads_last_30_days', sort: 'desc' },
    ]);

    const [totalOrganizations, setTotalOrganizations] = useState(0);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [podDiscrepancyFilter, setPodDiscrepancyFilter] = useState<string>('No filter');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [loadingPdfOrgId, setLoadingPdfOrgId] = useState<string | null>(null);

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error' as 'error' | 'success' | 'info' | 'warning',
    });

    const showError = (error: any, defaultMessage: string) => {
        const message = error?.response?.data?.message || defaultMessage;
        setSnackbar({
            open: true,
            message,
            severity: 'error',
        });
    };

    const handleOrgPdf = useCallback(async (org: Organization) => {
        setLoadingPdfOrgId(org.uuid);
        try {
            const primarySub: Subscription | undefined =
                (org.subscriptions ?? []).find((s) => s.name === 'fiyrpod_organization') ??
                (org.subscriptions ?? []).find((s) => s.name === 'organization') ??
                (org.subscriptions ?? [])[0];
            const status = primarySub?.stripe_status ?? undefined;
            await generateOrgSubscriptionReport({
                orgUuid: org.uuid,
                orgName: org.name,
                ownerName:
                    org.owner_first_name && org.owner_last_name
                        ? `${org.owner_first_name} ${org.owner_last_name}`
                        : undefined,
                ownerEmail: org.owner_email,
                ownerPhone: org.owner_phone,
                subscriptionTier: org.subscription_tier,
                subscriptionStatus: status,
                stripeCustomerId: org.stripe_id,
            });
        } catch (err) {
            console.error('Failed to generate org PDF', err);
            showError(err, 'Failed to generate PDF report');
        } finally {
            setLoadingPdfOrgId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBiPanelError = useCallback((message: string) => {
        setSnackbar({
            open: true,
            message,
            severity: 'error',
        });
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const hasPodDiscrepancy = (row: Organization): boolean => {
        return row.pods_org != row.org_pod_subscriptions;
    };

    const handlePaginationModelChange = (newModel: GridPaginationModel) => {
        setIsLoading(true);
        setPaginationModel(newModel);

        const updatedParams = {
            ...searchParams,
            page: newModel.page + 1,
            per_page: newModel.pageSize,
        };

        setSearchParams(updatedParams);

        getOrganizationsAndSubscriptionData(updatedParams)
            .then((response) => {
                setOrganizations(response.data.data);
                setTotalOrganizations(response.data.meta.total);
            })
            .catch((error) => {
                console.error('Failed to fetch organizations', error);
                showError(error, 'Failed to fetch organizations');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const fetchFilterOrganizations = debounce((searchTerm: string, perPage?: number) => {
        adminGetAllOrganizations(searchTerm, perPage)
            .then((response) => {
                setFilterOrganizations(response.data.data);
            })
            .catch((error) => {
                console.error('Failed to fetch organizations', error);
                showError(error, 'Failed to fetch organizations for filter');
            });
    }, 300);

    useEffect(() => {
        fetchFilterOrganizations('', 500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setIsLoading(true);
        getOrganizationsAndSubscriptionData({ ...searchParams, organization_uuid: '' })
            .then((response) => {
                setOrganizations(response.data.data);
                setTotalOrganizations(response.data.meta.total);
            })
            .catch((error) => {
                console.error('Failed to fetch organizations', error);
                showError(error, 'Failed to fetch organizations');
            })
            .finally(() => {
                setIsLoading(false);
            });
        // Intentionally run once on mount with initial searchParams (default sort: uploads last 30d desc).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatCentsToDollars = (cents: number): string => {
        const dollars = cents / 100;
        return `$${dollars.toFixed(2)}`;
    };

    const handlePodDiscrepancyChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setPodDiscrepancyFilter(value);
    };

    const handleProblematicAccountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchParams({
            ...searchParams,
            is_problematic_account: event.target.checked,
        });
    };

    const columns: GridColDef<Organization, any, any>[] = [
        {
            field: 'name',
            headerName: 'Name',
            width: 150,
            sortable: true,
            type: 'string',
            sortingOrder: ['asc', 'desc', null],
            renderCell: (params) => {
                const stripeId = params.row.stripe_id;

                if (stripeId) {
                    return (
                        <a
                            href={`https://dashboard.stripe.com/customers/${stripeId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', color: theme.palette.primary.main }}
                        >
                            {params.value}
                        </a>
                    );
                } else {
                    return <span>{params.value}</span>;
                }
            },
        },
        {
            field: 'created_at',
            headerName: 'Created At',
            width: 100,
            sortable: true,
            type: 'string',
            renderCell: (params) => {
                const date = new Date(params.value);
                return date.toISOString().split('T')[0];
            },
        },
        {
            field: 'sponsor_name',
            headerName: 'Sponsor',
            width: 150,
            sortable: false,
            type: 'string',
        },
        {
            field: 'athlete_count',
            headerName: 'Athlete Count',
            width: 120,
            sortable: true,
            type: 'number',
        },
        {
            field: 'subscription_tier',
            headerName: 'Current Tier',
            width: 120,
            sortable: true,
            type: 'string',
            renderCell: (params) => formatSubscriptionTierLabel(params.row.subscription_tier),
        },
        {
            field: 'stripe_sub_link',
            headerName: 'Stripe Sub',
            width: 175,
            sortable: false,
            renderCell: (params) => {
                const org: Organization = params.row as Organization;
                const subs: Subscription[] = org.subscriptions ?? [];
                const primarySub =
                    subs.find((s) => s.name === 'fiyrpod_organization') ??
                    subs.find((s) => s.name === 'organization') ??
                    subs[0];
                const isPdfLoading = loadingPdfOrgId === org.uuid;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {primarySub?.stripe_id ? (
                            <Button
                                size="small"
                                variant="outlined"
                                component="a"
                                href={`https://dashboard.stripe.com/subscriptions/${primarySub.stripe_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                endIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem !important' }} />}
                                sx={{
                                    fontSize: '0.6rem',
                                    py: 0.25,
                                    px: 0.75,
                                    minWidth: 0,
                                    textTransform: 'none',
                                }}
                            >
                                View
                            </Button>
                        ) : (
                            <Typography
                                variant="body2"
                                color="text.disabled"
                                sx={{ width: 48, fontSize: '0.6rem' }}
                            >
                                --
                            </Typography>
                        )}
                        <Tooltip title="Download subscription PDF report">
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    disabled={isPdfLoading}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOrgPdf(org);
                                    }}
                                    startIcon={
                                        isPdfLoading ? (
                                            <CircularProgress size={10} />
                                        ) : (
                                            <PictureAsPdfIcon
                                                sx={{ fontSize: '0.75rem !important' }}
                                            />
                                        )
                                    }
                                    sx={{
                                        fontSize: '0.6rem',
                                        py: 0.25,
                                        px: 0.75,
                                        minWidth: 0,
                                        textTransform: 'none',
                                    }}
                                >
                                    PDF
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: 'amount_past_due',
            headerName: 'Amount due',
            width: 120,
            sortable: true,
            type: 'number',
            renderCell: (params) => formatCentsToDollars(params.row.amount_past_due || 0),
        },
        {
            field: 'uploads_last_30_days',
            headerName: '30d',
            width: 120,
            sortable: true,
            sortingOrder: ['desc', 'asc', null],
            type: 'number',
            renderCell: (params) => {
                const uploadsPerMonth = params.row.uploads_per_month || {};
                const uploadsArray = Object.entries(uploadsPerMonth)
                    .map(([month, count]) => `${month}: ${count}`)
                    .join('\n');
                const last30 = params.row.uploads_last_30_days || 0;
                const last60 = params.row.uploads_last_60_days || 0;
                const prior30 = Math.max(0, last60 - last30);
                let icon = <TrendingFlatIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
                if (last30 > prior30) {
                    icon = <TrendingUpIcon fontSize="small" sx={{ color: 'success.main' }} />;
                } else if (last30 < prior30) {
                    icon = <TrendingDownIcon fontSize="small" sx={{ color: 'error.main' }} />;
                }

                return (
                    <Tooltip
                        title={
                            <pre style={{ fontSize: '0.5rem' }}>{uploadsArray || 'no data'}</pre>
                        }
                        arrow
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                            }}
                        >
                            <Typography variant="body2">{last30}</Typography>
                            {icon}
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'uploads_last_60_days',
            headerName: '60d',
            width: 120,
            sortable: true,
            sortingOrder: ['desc', 'asc', null],
            type: 'number',
        },
        {
            field: 'uploads_last_90_days',
            headerName: '90d',
            width: 120,
            sortable: true,
            sortingOrder: ['desc', 'asc', null],
            type: 'number',
        },
        {
            field: 'total_commissions',
            headerName: 'Total',
            width: 150,
            sortable: true,
            type: 'number',
            renderCell: (params) => formatCentsToDollars(params.row.total_commissions || 0),
        },
        {
            field: 'marketplace_commissions',
            headerName: 'Marketplace',
            width: 140,
            sortable: true,
            type: 'number',
            renderCell: (params) => formatCentsToDollars(params.row.marketplace_commissions || 0),
        },
        {
            field: 'pib_bonus_commissions',
            headerName: 'PIB Bonus',
            width: 140,
            sortable: true,
            type: 'number',
            renderCell: (params) => formatCentsToDollars(params.row.pib_bonus_commissions || 0),
        },
        {
            field: 'residual_commissions',
            headerName: 'Recurring',
            width: 140,
            sortable: true,
            type: 'number',
            renderCell: (params) => formatCentsToDollars(params.row.residual_commissions || 0),
        },
        {
            field: 'check_match_commissions',
            headerName: 'Check Match',
            width: 160,
            sortable: true,
            type: 'number',
            renderCell: (params) => formatCentsToDollars(params.row.check_match_commissions || 0),
        },
        {
            field: 'pods_org',
            headerName: 'Org Pods',
            width: 100,
            sortable: true,
            type: 'number',
            renderCell: (params) => {
                if (hasPodDiscrepancy(params.row as Organization)) {
                    return (
                        <Box
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'right' }}
                        >
                            {params.value}
                            <Tooltip
                                title="This organization pod count and subscription count do not match."
                                arrow
                                placement="top"
                            >
                                <InfoIcon
                                    sx={{ color: 'warning.main', fontSize: '1rem', ml: 0.5 }}
                                />
                            </Tooltip>
                        </Box>
                    );
                }
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'right' }}>
                        {params.value}
                    </Box>
                );
            },
            align: 'center',
        },
        {
            field: 'org_pod_subscriptions',
            headerName: 'Org Pod Subscriptions',
            width: 140,
            sortable: true,
            type: 'number',
        },
        {
            field: 'pods_athlete',
            headerName: 'Athlete Pods',
            width: 100,
            sortable: true,
            type: 'number',
        },
        {
            field: 'athlete_pod_subscriptions',
            headerName: 'Athlete Pod Subscriptions',
            width: 140,
            sortable: true,
            type: 'number',
        },
    ];

    const handleOrgSearch = async (event: React.FormEvent) => {
        setIsLoading(true);
        event.preventDefault();
        try {
            await getOrgSearchResults(searchParams);
        } catch (error) {
            console.error(error);
            showError(error, 'Failed to search organizations');
        } finally {
            setIsLoading(false);
        }
    };

    const handleExportCSV = async () => {
        setIsExporting(true);
        try {
            const queryParams = { ...searchParams };

            if (podDiscrepancyFilter !== 'No filter') {
                queryParams.org_pod_discrepancy = podDiscrepancyFilter;
            } else {
                delete queryParams.org_pod_discrepancy;
            }

            // Remove pagination params for CSV export
            delete queryParams.page;
            delete queryParams.per_page;

            const response = await getOrganizationsAndSubscriptionDataCSV(queryParams);

            // Create a Blob from the response data
            const blob = new Blob([response.data], { type: 'text/csv' });

            // Create a temporary URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Build filename with date range if applicable
            let filename = 'organizations-export';
            if (searchParams.start_date || searchParams.end_date) {
                const start = searchParams.start_date || 'beginning';
                const end = searchParams.end_date || 'now';
                filename += `_date_range_${start}_to_${end}`;
            }
            filename += `_${new Date().toISOString().slice(0, 10)}.csv`;

            // Create a temporary anchor element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setSnackbar({
                open: true,
                message: 'CSV exported successfully',
                severity: 'success',
            });
        } catch (error) {
            console.error('Failed to export CSV', error);
            showError(error, 'Failed to export CSV');
        } finally {
            setIsExporting(false);
        }
    };

    const getOrgSearchResults = async (params: any) => {
        const queryParams = { ...params };

        if (podDiscrepancyFilter !== 'No filter') {
            queryParams.org_pod_discrepancy = podDiscrepancyFilter;
        } else {
            delete queryParams.org_pod_discrepancy;
        }

        if (!queryParams.start_date) {
            delete queryParams.start_date;
        }
        if (!queryParams.end_date) {
            delete queryParams.end_date;
        }
        if (!queryParams.is_problematic_account) {
            delete queryParams.is_problematic_account;
        }

        const response = await getOrganizationsAndSubscriptionData(queryParams);

        if (Array.isArray(response.data.data)) {
            const updatedOrgs = response.data.data.map((org: Organization) => {
                return {
                    ...org,
                };
            });
            setOrganizations(updatedOrgs);
            setTotalOrganizations(response.data.meta.total);
        } else {
            console.error('Unable to get organization search results');
            throw new Error('Unable to get organization search results');
        }
    };

    const handleOrgSort = async (model: GridSortModel) => {
        setIsLoading(true);
        let sortField = model[0]?.field;
        const sortOrder = model[0]?.sort;

        if (!sortField || sortOrder == null) {
            setSortModel([{ field: 'uploads_last_30_days', sort: 'desc' }]);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            const params = {
                ...searchParams,
                sort: '-uploads_last_30_days' as GetOrganizationsAndSubscriptionDataParams['sort'],
                page: 1,
            };
            setSearchParams(params);
            try {
                await getOrgSearchResults(params);
            } catch (error) {
                console.error('Failed to fetch sorted organizations', error);
                showError(error, 'Failed to fetch sorted organizations');
            } finally {
                setIsLoading(false);
            }
            return;
        }

        setSortModel(model);

        if (sortField === 'subscription_tier') {
            sortField = 'subscription_tier_sort';
        }

        if (sortOrder === 'desc') {
            sortField = `-${sortField}`;
        }

        const params = {
            ...searchParams,
            sort: sortField as GetOrganizationsAndSubscriptionDataParams['sort'],
            page: 1,
        };

        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        setSearchParams(params);

        try {
            await getOrgSearchResults(params);
        } catch (error) {
            console.error('Failed to fetch sorted organizations', error);
            showError(error, 'Failed to fetch sorted organizations');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOrgBiTierChange = (event: SelectChangeEvent<unknown>) => {
        setSearchParams({
            ...searchParams,
            tier: event.target.value as GetOrganizationsAndSubscriptionDataParams['tier'],
        });
    };

    const handleViewChange = (event: SelectChangeEvent<SelectedView>) => {
        setSelectedView(event.target.value as SelectedView);
    };

    const handleCreateOrganization = async (organizationData: CreateOrganizationParams) => {
        setIsLoading(true);
        try {
            await createOrganization(organizationData);
            setSnackbar({
                open: true,
                message: 'Organization created successfully',
                severity: 'success',
            });
            // Refresh the organizations list
            const response = await getOrganizationsAndSubscriptionData(searchParams);
            setOrganizations(response.data.data);
            setTotalOrganizations(response.data.meta.total);
        } catch (error: any) {
            console.error('Failed to create organization', error);
            showError(error, 'Failed to create organization');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="relative">
            {isLoading && (
                <div
                    style={{
                        position: 'fixed',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 9999,
                    }}
                >
                    <div className="bg-white rounded-lg p-4 shadow-lg flex flex-col items-center gap-2">
                        <CircularProgress size={80} />
                    </div>
                </div>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                message={snackbar.message}
            />

            <Box sx={{ margin: theme.spacing(0, 2) }}>
                {selectedView === 'metrics' && (
                    <OrganizationsMetricsBiPanel
                        searchParams={searchParams}
                        podDiscrepancyFilter={podDiscrepancyFilter}
                        onApiError={handleBiPanelError}
                    />
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 2,
                    }}
                >
                    <Box>
                        <h2 style={{ marginBottom: '4px' }}>Organizations</h2>
                        <p
                            style={{
                                fontSize: '0.9rem',
                                color: theme.palette.text.secondary,
                                marginTop: '0px',
                            }}
                        >
                            View organization metrics and maps
                        </p>
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                        sx={{ mt: 1 }}
                    >
                        Create Organization
                    </Button>
                </Box>

                <Box sx={{ marginBottom: 3, maxWidth: '240px' }}>
                    <FormControl fullWidth>
                        <InputLabel id="view-selection-label">View</InputLabel>
                        <Select
                            labelId="view-selection-label"
                            value={selectedView}
                            onChange={handleViewChange}
                            label="View"
                            size="small"
                        >
                            <MenuItem value="metrics">Metrics</MenuItem>
                            <MenuItem value="trends">Subscription Trends</MenuItem>
                            <MenuItem value="mrr_movement">MRR Movement</MenuItem>
                            <MenuItem value="maps">Maps</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {selectedView === 'metrics' && (
                    <>
                        <section style={{ marginBottom: theme.spacing(2) }}>
                            <h4>Metrics</h4>
                            <p
                                style={{
                                    fontSize: '0.8rem',
                                    color: theme.palette.text.secondary,
                                    marginTop: '-8px',
                                    marginBottom: '0px',
                                }}
                            >
                                Filter by organization, subscription tier, pod discrepancies. You
                                may also filter by date range to see data for a specific period.
                            </p>
                            <Box sx={{ mb: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={searchParams.is_problematic_account || false}
                                            onChange={handleProblematicAccountChange}
                                            size="small"
                                        />
                                    }
                                    label="View only problematic accounts (accounts with uploads this month that also have unpaid subscriptions)"
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.7rem',
                                            fontStyle: 'italic',
                                            color: theme.palette.text.secondary,
                                        },
                                    }}
                                />
                            </Box>
                            <ResponsiveForm onSubmit={handleOrgSearch} noValidate>
                                <ResponsiveFormItem>
                                    <Autocomplete
                                        value={
                                            filterOrganizations.find(
                                                (org) =>
                                                    org.uuid === searchParams.organization_uuid,
                                            ) || null
                                        }
                                        onChange={(event, newValue) => {
                                            setSearchParams({
                                                ...searchParams,
                                                organization_uuid: newValue ? newValue.uuid : '',
                                            });
                                        }}
                                        onInputChange={(event, newInputValue, reason) => {
                                            if (reason !== 'reset') {
                                                fetchFilterOrganizations(newInputValue);
                                            }
                                        }}
                                        getOptionKey={(option) => option.uuid}
                                        filterOptions={(options) => options}
                                        size={'small'}
                                        style={{ width: '250px' }}
                                        fullWidth
                                        options={filterOrganizations}
                                        getOptionLabel={(option) =>
                                            `${option.name} - ${option.uuid}`
                                        }
                                        renderInput={(params) => (
                                            <TextField {...params} label="Organizations" />
                                        )}
                                    />
                                </ResponsiveFormItem>
                                <ResponsiveFormItem>
                                    <FormControl size="small">
                                        <InputLabel>Current Tier</InputLabel>
                                        <Select
                                            label="Current Tier"
                                            id="tier-select"
                                            variant="outlined"
                                            size={'small'}
                                            value={searchParams.tier}
                                            onChange={handleOrgBiTierChange}
                                            sx={{ width: '150px' }}
                                        >
                                            <MenuItem value="all">All</MenuItem>
                                            <MenuItem value="gold">Gold</MenuItem>
                                            <MenuItem value="silver">Silver</MenuItem>
                                            <MenuItem value="bronze">Bronze</MenuItem>
                                            <MenuItem value="no_tier">No Tier</MenuItem>
                                        </Select>
                                    </FormControl>
                                </ResponsiveFormItem>
                                <ResponsiveFormItem>
                                    <FormControl size="small" sx={{ mb: 2, minWidth: 300 }}>
                                        <InputLabel>Pod Discrepancy Filter</InputLabel>
                                        <Select
                                            value={podDiscrepancyFilter || ''}
                                            onChange={handlePodDiscrepancyChange}
                                            label="Pod Discrepancy Filter"
                                            size="small"
                                        >
                                            <MenuItem value="No filter" selected>
                                                No filter
                                            </MenuItem>
                                            <MenuItem value="true">
                                                Show only organizations with pod discrepancies
                                            </MenuItem>
                                            <MenuItem value="false">
                                                Show only organizations without pod discrepancies
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </ResponsiveFormItem>
                                <ResponsiveFormItem>
                                    <TextField
                                        label="Start Date"
                                        type="date"
                                        size="small"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        value={searchParams.start_date || ''}
                                        onChange={(e) =>
                                            setSearchParams({
                                                ...searchParams,
                                                start_date: e.target.value,
                                            })
                                        }
                                        sx={{ width: '180px' }}
                                    />
                                </ResponsiveFormItem>
                                <ResponsiveFormItem>
                                    <TextField
                                        label="End Date"
                                        type="date"
                                        size="small"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        value={searchParams.end_date || ''}
                                        onChange={(e) =>
                                            setSearchParams({
                                                ...searchParams,
                                                end_date: e.target.value,
                                            })
                                        }
                                        sx={{ width: '180px' }}
                                    />
                                </ResponsiveFormItem>
                                <ResponsiveFormItem>
                                    <Button
                                        type="submit"
                                        size={'medium'}
                                        variant="contained"
                                        fullWidth={isNarrow}
                                    >
                                        Search Organizations
                                    </Button>
                                </ResponsiveFormItem>
                                <ResponsiveFormItem>
                                    <Button
                                        onClick={handleExportCSV}
                                        size="medium"
                                        variant="outlined"
                                        disabled={isExporting}
                                        startIcon={
                                            isExporting ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <FileDownloadIcon />
                                            )
                                        }
                                        fullWidth={isNarrow}
                                    >
                                        {isExporting ? 'Exporting...' : 'Export as CSV'}
                                    </Button>
                                </ResponsiveFormItem>
                            </ResponsiveForm>
                        </section>
                        <Box
                            width="100%"
                            sx={{
                                marginBottom: theme.spacing(6),
                                '& .pod-discrepancy-row': {
                                    backgroundColor: 'rgba(255, 0, 0, 0.1) !important',
                                },
                                '& .MuiDataGrid-row.pod-discrepancy-row': {
                                    backgroundColor: 'rgba(255, 0, 0, 0.1) !important',
                                },
                            }}
                        >
                            <DataGridPremium
                                rows={organizations}
                                columns={columns}
                                columnGroupingModel={ORG_METRICS_COLUMN_GROUPING_MODEL}
                                checkboxSelection={false}
                                sortingMode="server"
                                sortingOrder={['desc', 'asc', null]}
                                sortModel={sortModel}
                                onSortModelChange={handleOrgSort}
                                getRowId={(row) => row.id}
                                autoHeight
                                pagination
                                pageSizeOptions={[10, 20, 50, 100]}
                                paginationModel={paginationModel}
                                onPaginationModelChange={handlePaginationModelChange}
                                rowCount={totalOrganizations}
                                paginationMode="server"
                                disableColumnMenu={true}
                                showCellVerticalBorder={true}
                                getRowClassName={(params) =>
                                    hasPodDiscrepancy(params.row as Organization)
                                        ? 'pod-discrepancy-row'
                                        : ''
                                }
                                sx={{
                                    '& .MuiDataGrid-cell:focus': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-cell:focus-within': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        fontSize: '0.6rem',
                                    },
                                    '& .MuiDataGrid-columnGroupHeader, & .MuiDataGrid-columnHeader--filledGroup':
                                        {
                                            fontSize: '0.68rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.045em',
                                            lineHeight: 1.25,
                                            color: theme.palette.primary.dark,
                                            backgroundColor: alpha(
                                                theme.palette.primary.main,
                                                0.12,
                                            ),
                                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                                            boxShadow: `inset 0 1px 0 ${alpha(
                                                theme.palette.common.black,
                                                0.06,
                                            )}`,
                                            /* Left edge of each category band — aligns with leaf headers + body cells */
                                            borderLeft: `2px solid ${alpha(
                                                theme.palette.primary.main,
                                                0.5,
                                            )}`,
                                            paddingLeft: theme.spacing(1.25),
                                            paddingRight: theme.spacing(1.25),
                                        },
                                    '& .MuiDataGrid-columnGroupHeader .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitle':
                                        {
                                            fontWeight: 700,
                                            color: theme.palette.primary.dark,
                                        },
                                    /* Full-height category dividers (same x as group row) */
                                    '& .MuiDataGrid-columnHeader[data-field="name"], & .MuiDataGrid-columnHeader[data-field="subscription_tier"], & .MuiDataGrid-columnHeader[data-field="uploads_last_30_days"], & .MuiDataGrid-columnHeader[data-field="total_commissions"], & .MuiDataGrid-columnHeader[data-field="pods_org"], & .MuiDataGrid-cell[data-field="name"], & .MuiDataGrid-cell[data-field="subscription_tier"], & .MuiDataGrid-cell[data-field="uploads_last_30_days"], & .MuiDataGrid-cell[data-field="total_commissions"], & .MuiDataGrid-cell[data-field="pods_org"]':
                                        {
                                            borderLeft: `2px solid ${alpha(
                                                theme.palette.primary.main,
                                                0.5,
                                            )}`,
                                            boxSizing: 'border-box',
                                        },
                                    '& .MuiDataGrid-columnHeader': {
                                        fontSize: '0.6rem',
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        fontSize: '0.6rem',
                                    },
                                    '& .MuiDataGrid-footerContainer': {
                                        fontSize: '0.6rem',
                                    },
                                    '& .pod-discrepancy-row': {
                                        backgroundColor: 'rgba(255, 0, 0, 0.1) !important',
                                    },
                                    '& .MuiDataGrid-row.pod-discrepancy-row': {
                                        backgroundColor: 'rgba(255, 0, 0, 0.1) !important',
                                    },
                                }}
                            />
                        </Box>
                    </>
                )}

                {selectedView === 'trends' && (
                    <section style={{ marginBottom: theme.spacing(2) }}>
                        <SubscriptionTrends />
                    </section>
                )}

                {selectedView === 'mrr_movement' && (
                    <section style={{ marginBottom: theme.spacing(2) }}>
                        <MrrMovement />
                    </section>
                )}

                {selectedView === 'maps' && (
                    <section style={{ marginBottom: theme.spacing(2) }}>
                        <h4>Organization Map</h4>
                        <OrganizationMap />
                    </section>
                )}
            </Box>

            <CreateOrganizationDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSubmit={handleCreateOrganization}
            />
        </div>
    );
};

export default OrganizationsList;
